import {annotateData, type EBirdMyDataSchema} from "ebird-mydata-reader";

export const STORED_EBIRD_DATA_KEY = 'storedEbirdData';
export const STORED_EBIRD_DATA_VERSION = 3;

export type StoredObservationRecord = {
    submissionId?: string,
    submissionID?: string,
    commonName: string,
    scientificName: string,
    taxonomicOrder: number,
    count?: string | number,
    stateProvince?: string,
    county?: string,
    locationId?: string,
    location?: string,
    latitude?: string | number,
    longitude?: string | number,
    date: string,
    time?: string,
    protocol?: string,
    durationMin?: string | number,
    allObsReported?: boolean | number,
    mlCatalogNumbers?: string,
    observedYear?: number,
    observedMonth?: number,
    baseScientificName?: string,
    canonicalTaxonomicOrder?: number,
    reportAsTaxonomicOrder?: number,
    reportAsCommonName?: string,
    reportAsScientificName?: string,
    isLifer?: boolean,
    isFirstOfYear?: boolean
}

export type StoredBirdDataPayload = {
    version: number,
    date: string,
    signature: string,
    data: StoredObservationRecord[]
}

export function hydrateStoredObservationData(data: StoredObservationRecord[]): EBirdMyDataSchema[] {
    return annotateData(data as EBirdMyDataSchema[]);
}

export function compactObservationData(rawData: EBirdMyDataSchema[]): StoredObservationRecord[] {
    return rawData.map(observation => {
        const submissionId = observation.submissionId ?? observation.submissionID;

        return {
            submissionId,
            submissionID: submissionId,
            commonName: observation.commonName,
            scientificName: observation.scientificName,
            taxonomicOrder: observation.taxonomicOrder,
            count: observation.count,
            stateProvince: observation.stateProvince,
            county: observation.county,
            locationId: observation.locationId,
            location: observation.location,
            latitude: observation.latitude,
            longitude: observation.longitude,
            date: observation.date,
            time: observation.time,
            protocol: observation.protocol,
            durationMin: observation.durationMin,
            allObsReported: observation.allObsReported,
            mlCatalogNumbers: observation.mlCatalogNumbers,
            observedYear: observation.observedYear,
            observedMonth: observation.observedMonth,
            baseScientificName: observation.baseScientificName,
            canonicalTaxonomicOrder: observation.canonicalTaxonomicOrder,
            reportAsTaxonomicOrder: observation.reportAsTaxonomicOrder,
            reportAsCommonName: observation.reportAsCommonName,
            reportAsScientificName: observation.reportAsScientificName,
            isLifer: observation.isLifer,
            isFirstOfYear: observation.isFirstOfYear
        };
    });
}

export function getYearsFromStoredBirdData(data: Array<{date?: string}>): number[] {
    const years = new Set<number>();

    for (const row of data) {
        if (!row.date) continue;

        const year = parseInt(row.date.split('-')[0], 10);
        if (Number.isFinite(year)) {
            years.add(year);
        }
    }

    return Array.from(years).sort((left, right) => right - left);
}

export function buildObservationDatasetSignature(data: Array<Partial<StoredObservationRecord>>) {
    if (!data.length) {
        return 'empty';
    }

    const firstObservation = data[0];
    const lastObservation = data[data.length - 1];
    const firstSignature = [
        firstObservation.date,
        firstObservation.time,
        firstObservation.taxonomicOrder,
        firstObservation.locationId,
        firstObservation.submissionId ?? firstObservation.submissionID
    ].join('|');
    const lastSignature = [
        lastObservation.date,
        lastObservation.time,
        lastObservation.taxonomicOrder,
        lastObservation.locationId,
        lastObservation.submissionId ?? lastObservation.submissionID
    ].join('|');

    return `${data.length}:${firstSignature}:${lastSignature}`;
}
